import { Howl } from 'howler';

/**
 * Notification Utilities
 * 
 * Sound effects and notifications for game events.
 * "Because FOMO sounds better with audio cues." 🔊
 */

// Sound effects (using placeholder URLs - replace with actual sound files)
const SOUNDS = {
  buy: new Howl({
    src: ['/sounds/buy.mp3', '/sounds/buy.ogg'],
    volume: 0.5,
    preload: true,
  }),
  
  timer: new Howl({
    src: ['/sounds/timer.mp3', '/sounds/timer.ogg'],
    volume: 0.3,
    preload: true,
  }),
  
  jackpot: new Howl({
    src: ['/sounds/jackpot.mp3', '/sounds/jackpot.ogg'],
    volume: 0.7,
    preload: true,
  }),
  
  roundEnd: new Howl({
    src: ['/sounds/round-end.mp3', '/sounds/round-end.ogg'],
    volume: 0.6,
    preload: true,
  }),
  
  claim: new Howl({
    src: ['/sounds/claim.mp3', '/sounds/claim.ogg'],
    volume: 0.5,
    preload: true,
  }),
};

// User preference for sound
let soundEnabled = true;

/**
 * Play a sound effect
 */
export function playSound(type: keyof typeof SOUNDS): void {
  if (!soundEnabled) return;
  
  try {
    const sound = SOUNDS[type];
    if (sound) {
      sound.play();
    }
  } catch (error) {
    console.log('Sound playback failed:', error);
  }
}

/**
 * Toggle sound on/off
 */
export function toggleSound(): boolean {
  soundEnabled = !soundEnabled;
  return soundEnabled;
}

/**
 * Get current sound state
 */
export function isSoundEnabled(): boolean {
  return soundEnabled;
}

/**
 * Request notification permission
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

/**
 * Send a browser notification
 */
export function sendNotification(title: string, options?: NotificationOptions): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return;
  }

  try {
    new Notification(title, {
      icon: '/icon-192.png',
      badge: '/icon-192.png',
      ...options,
    });
  } catch (error) {
    console.log('Notification failed:', error);
  }
}

/**
 * Notify user of important game events
 */
export function notifyGameEvent(type: 'lowTimer' | 'roundEnd' | 'jackpot' | 'newRound', data?: any): void {
  const messages = {
    lowTimer: {
      title: '⏰ Timer Running Low!',
      body: 'Less than 5 minutes left! Someone could win soon...',
    },
    roundEnd: {
      title: '🏆 Round Ended!',
      body: data?.winner 
        ? `Winner: ${data.winner.slice(0, 8)}... took home the pot!`
        : 'A new winner has been crowned!',
    },
    jackpot: {
      title: '🎰 JACKPOT TRIGGERED!',
      body: 'Someone hit the jackpot bonus! Check the results!',
    },
    newRound: {
      title: '🔄 New Round Started!',
      body: 'Fresh round, fresh pot, fresh FOMO. Time to play!',
    },
  };

  const msg = messages[type];
  if (msg) {
    sendNotification(msg.title, { body: msg.body });
    
    // Also play appropriate sound
    if (type === 'lowTimer') playSound('timer');
    else if (type === 'roundEnd') playSound('roundEnd');
    else if (type === 'jackpot') playSound('jackpot');
  }
}
